import dayjs, { Dayjs } from "dayjs"
import {
  IMeterAppointment,
  IMeterAppointmentSurvey,
  MeterAppointmentStatus,
  meterTypesText,
  appointmentTypesText,
  installationTypesText,
  IMeterAppointmentEvent,
  IMeterAppointmentComment,
  IAbortReasonEntry,
} from "./meterAppointment.types"
import { Status } from "@/types/Status"
import { SoCarouselItem } from "@/components/SoCarousel.vue"
import { getFullDate, formatTimeSlot } from "@/helpers/dateHelper"

export class MeterAppointment implements IMeterAppointment {
  id: number
  slotStartDateTime: Dayjs
  slotEndDateTime: Dayjs
  mpan: string
  mprn: string
  engineerName: string
  engineerEmail: string
  appointmentId: string
  electricMeterSerialNumber: string
  gasMeterSerialNumber: string
  displayJobNumber: string
  aesJobType: string
  aesStatus: MeterAppointmentStatus | null
  appointmentType: string
  meterType: string
  installationType: string
  timeSlot: string
  createdAt: Dayjs
  cancelledDateTime?: Dayjs
  installedDateTime?: Dayjs
  failedDateTime?: Dayjs
  survey?: IMeterAppointmentSurvey[]
  history?: IMeterAppointment[]
  events?: IMeterAppointmentEvent[]
  comments?: IMeterAppointmentComment[]
  abortReason?: string
  cancelReason?: string
  mop?: string

  constructor(
    id: number,
    slotStartDateTime: Dayjs,
    slotEndDateTime: Dayjs,
    mpan: string,
    mprn: string,
    engineerName: string,
    engineerEmail: string,
    appointmentId: string,
    electricMeterSerialNumber: string,
    gasMeterSerialNumber: string,
    displayJobNumber: string,
    aesJobType: string,
    aesStatus: MeterAppointmentStatus | null,
    appointmentType: string,
    meterType: string,
    installationType: string,
    timeSlot: string,
    createdAt: Dayjs,
    cancelledDateTime?: Dayjs,
    installedDateTime?: Dayjs,
    failedDateTime?: Dayjs,
    survey?: IMeterAppointmentSurvey[],
    history?: IMeterAppointment[],
    events?: IMeterAppointmentEvent[],
    comments?: IMeterAppointmentComment[],
    abortReason?: string,
    cancelReason?: string,
    mop?: string,
  ) {
    this.id = id
    this.slotStartDateTime = slotStartDateTime
    this.slotEndDateTime = slotEndDateTime
    this.mpan = mpan
    this.mprn = mprn
    this.engineerName = engineerName
    this.engineerEmail = engineerEmail
    this.appointmentId = appointmentId
    this.electricMeterSerialNumber = electricMeterSerialNumber
    this.gasMeterSerialNumber = gasMeterSerialNumber
    this.displayJobNumber = displayJobNumber
    this.aesJobType = aesJobType
    this.aesStatus = aesStatus || MeterAppointmentStatus.BOOKED
    this.appointmentType = appointmentType
    this.meterType = meterType
    this.installationType = installationType
    this.timeSlot = timeSlot
    this.createdAt = createdAt
    this.cancelledDateTime = cancelledDateTime
    this.installedDateTime = installedDateTime
    this.failedDateTime = failedDateTime
    this.survey = survey
    this.history = history
    this.events = events
    this.comments = comments
    this.abortReason = abortReason
    this.cancelReason = cancelReason
    this.mop = mop
  }

  get status(): Status {
    switch (this.aesStatus as MeterAppointmentStatus) {
      case MeterAppointmentStatus.BOOKED:
        return new Status("neutral", MeterAppointmentStatus.BOOKED)
      case MeterAppointmentStatus.RESCHEDULED:
        return new Status("default", MeterAppointmentStatus.RESCHEDULED)
      case MeterAppointmentStatus.CANCELLED:
        return new Status("caution", MeterAppointmentStatus.CANCELLED)
      case MeterAppointmentStatus.ABORTED:
        return new Status("critical", MeterAppointmentStatus.ABORTED)
      case MeterAppointmentStatus.COMPLETED:
        return new Status("normal", MeterAppointmentStatus.COMPLETED)
      default:
        return new Status("default", this.aesStatus as string)
    }
  }

  get engineerDetails(): string {
    let text = this.engineerName

    if (this.engineerEmail) {
      text += ` (${this.engineerEmail})`
    }

    return text
  }

  get slotDate(): string {
    return getFullDate(dayjs(this.slotStartDateTime))
  }

  get timeSlotRange(): string {
    return formatTimeSlot(this.slotStartDateTime, this.slotEndDateTime)
  }

  get installer(): string {
    return this.mop || "AES"
  }

  get title(): string {
    const jobPrefix = this.displayJobNumber ? `${this.displayJobNumber} - ` : ""
    switch (this.aesStatus) {
      case MeterAppointmentStatus.BOOKED:
        return `${jobPrefix}New ${this.installer} site visit booked for ${this.slotDate} - ${this.description}`
      case MeterAppointmentStatus.RESCHEDULED:
        return `${jobPrefix}${this.installer} site visit rescheduled for ${this.slotDate} - ${this.description}`
      case MeterAppointmentStatus.CANCELLED:
        return `${jobPrefix}${this.installer} site visit cancelled for ${this.slotDate} - ${this.description}`
      case MeterAppointmentStatus.ABORTED:
        return `${jobPrefix}Aborted ${this.installer} site visit on ${this.slotDate} - ${this.description}`
      case MeterAppointmentStatus.COMPLETED:
        return `${jobPrefix}Completed ${this.installer} site visit on ${this.slotDate} - ${this.description}`
      default:
        return `${jobPrefix}${this.installer} appointment on ${this.slotDate} - ${this.description}`
    }
  }

  get removedMeterReads(): IMeterAppointmentSurvey[] {
    if (!this.survey?.length) return []
    return this.survey.filter(
      (item) =>
        item.question?.includes("Removed") &&
        item.question?.includes("Read") &&
        !item.question?.includes("Image") &&
        item.answer?.length,
    )
  }

  /**
   * Filters and returns survey notes that do not contain image references.
   *
   * Note: The `answer` field in a survey note can contain either a textual response or an image path.
   * If the `answer` contains an image path (e.g., starting with "aes/images"), it is excluded from the results.
   * The `imageUrl` field, if populated, contains the full URL to an image and also indicates an image reference.
   */
  get notes(): IMeterAppointmentSurvey[] {
    if (!this.survey?.length) return []
    return this.survey?.filter(
      (note) =>
        note.answer?.length &&
        !note.imageUrl?.length &&
        !note.answer.includes("aes/images"),
    )
  }

  get description(): string {
    const installation = installationTypesText.find(
      (install) => install.value === this.installationType,
    )
    const appointment = appointmentTypesText.find(
      (install) => install.value === this.appointmentType,
    )
    const meter = meterTypesText.find(
      (install) => install.value === this.meterType,
    )

    return [
      installation?.text,
      installation?.text ? "-" : "",
      meter?.text,
      appointment?.text,
    ].join(" ")
  }

  get abortImageFromSurvey(): string | null {
    if (this.aesStatus !== MeterAppointmentStatus.ABORTED) return null

    return (
      this.survey
        ?.filter((item) => item.name === "Abort")
        .find((item) => item.imageUrl.length)?.imageUrl ?? null
    )
  }

  get parsedAbortReason(): IAbortReasonEntry[] {
    if (!this.abortReason) return []
    return JSON.parse(this.abortReason)
  }

  get surveyImages(): SoCarouselItem[] {
    if (!this.survey?.length) return []
    return this.survey
      .filter((survey) => survey.imageUrl)
      .map((survey) => ({
        src: survey.imageUrl,
        text: survey.question,
      }))
  }
}
